import { useLocalSearchParams } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { useFleetClient, type ClientAddress, type ClientDetail } from "@visioni/a3s-monitoring/app";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { Empty, Field, InlineLink, Section } from "../components/DetailSection";
import { ClientActionPlans } from "../components/ClientActionPlans";
import { EMPTY, formatInstant, formatText, hasText } from "../format";
import { clientActivationsHref, clientIncidentsHref, clientOperatorNotesHref } from "../routes";

/**
 * One Patriot client, as the replica holds it: who it is, how to reach the
 * premises, what operators are told to do, how Patriot groups it, and which
 * action plan runs for each kind of signal.
 */
export function FleetClientScreen() {
  const { tenantId, clientNo = "" } = useLocalSearchParams<{ tenantId: string; clientNo?: string }>();
  const { client, isLoading, error, refetch } = useFleetClient(clientNo);

  return (
    <PatriotToolsPage
      title={client !== undefined && hasText(client.name) ? client.name : `Client ${clientNo}`}
      {...(client !== undefined && { subtitle: client.clientNo })}
    >
      {client === undefined ?
        <QueryNotice isLoading={isLoading} error={error} onRetry={refetch} />
      : <ClientDetailView tenantId={tenantId} client={client} />}
    </PatriotToolsPage>
  );
}

function ClientDetailView({ tenantId, client }: { tenantId: string; client: ClientDetail }) {
  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Client">{client.clientNo}</Field>
        <Field label="Name">{formatText(client.name)}</Field>
        <Field label="Description">{formatText(client.description)}</Field>
        <Field label="Attend alarms">
          {client.attendAlarm === undefined ?
            EMPTY
          : client.attendAlarm ?
            "Yes"
          : "No"}
        </Field>
      </Section>

      <Section title="Related">
        <InlineLink href={clientActivationsHref(tenantId, client.clientNo)}>Activations for this client</InlineLink>
        <InlineLink href={clientIncidentsHref(tenantId, client.clientNo)}>Genuine incidents for this client</InlineLink>
        <InlineLink href={clientOperatorNotesHref(tenantId, client.clientNo)}>
          Operator notes for this client
        </InlineLink>
      </Section>

      <Section title="Premises">
        <Field label="Address">{formatAddress(client.address)}</Field>
        <Field label="Site phone">{formatText(client.sitePhone)}</Field>
        <Field label="Site mobile">{formatText(client.siteMobile)}</Field>
        <Field label="Email">{formatText(client.email)}</Field>
      </Section>

      <Section title="Response instructions">
        {hasText(client.responseInstructions) ?
          // Free text written for the operator; its line breaks are its layout.
          <Text textStyle="sm/regular" color="text" selectable>
            {client.responseInstructions}
          </Text>
        : <Empty>No response instructions.</Empty>}
      </Section>

      <Section title={`Groupings (${String(client.clientGroupings.length)})`}>
        {client.clientGroupings.length === 0 ?
          <Empty>Not in any grouping.</Empty>
        : client.clientGroupings.map((grouping, index) => (
            <View key={grouping.id} style={[styles.entry, index > 0 && styles.entryDivider]}>
              {/* An id in place of a name means the replica has not copied that record yet. */}
              <Field label="Type">
                {grouping.type === undefined ? EMPTY : (grouping.type.name ?? `Type ${grouping.type.id}`)}
              </Field>
              <Field label="Name">{grouping.name ?? `Grouping ${grouping.id}`}</Field>
            </View>
          ))
        }
      </Section>

      <ClientActionPlans client={client} />

      <Section title="Copy in a3s">
        <Field label="First copied">{formatInstant(client.a3sFirstSeenAt)}</Field>
        <Field label="Last changed">{formatInstant(client.a3sLastWrittenAt)}</Field>
      </Section>
    </View>
  );
}

function formatAddress(address: ClientAddress | undefined): string {
  if (address === undefined) return EMPTY;
  const locality = [address.city, address.state, address.postcode].filter(hasText).join(" ");
  const lines = [...address.addressLines, locality, address.country].filter(hasText);
  return lines.length === 0 ? EMPTY : lines.join("\n");
}

const styles = StyleSheet.create((theme) => ({
  entry: { gap: theme.spacing.xs, paddingTop: theme.spacing.md },
  entryDivider: { borderTopWidth: 1, borderColor: theme.colors.borderCard },
}));
